import React, { useState } from 'react';

const STRUCTURAL_TYPE_CONFIG = {
  add_chapter:      { label: 'Add Chapter',      color: '#22c55e' },
  delete_chapter:   { label: 'Delete Chapter',   color: '#ef4444' },
  merge_chapters:   { label: 'Merge Chapters',   color: '#f59e0b' },
  split_chapter:    { label: 'Split Chapter',     color: '#3b82f6' },
  reorder_chapters: { label: 'Reorder Chapters',  color: '#8b5cf6' },
};

const THEMATIC_TYPE_CONFIG = {
  tone_shift:       { label: 'Tone Shift',       color: '#ec4899' },
  theme_emphasis:   { label: 'Theme Emphasis',   color: '#f59e0b' },
  motif_tracking:   { label: 'Motif Tracking',   color: '#06b6d4' },
  theme_removal:    { label: 'Theme Removal',     color: '#ef4444' },
  theme_addition:   { label: 'Theme Addition',    color: '#22c55e' },
  pov_consistency:  { label: 'POV Consistency',   color: '#8b5cf6' },
};

const SEVERITY_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#f59e0b',
  low: '#22c55e',
};

const SCOPE_BADGE = {
  structural: { label: 'Structural', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
  thematic:   { label: 'Thematic',   color: '#ec4899', bg: 'rgba(236,72,153,0.1)' },
};

export default function ProjectIssueCard({ issue, onApprove, onDismiss, isBusy, error }) {
  const [isExpanded, setIsExpanded] = useState(issue.status === 'pending');

  const typeConfig = issue.scope === 'structural'
    ? STRUCTURAL_TYPE_CONFIG[issue.issue_type]
    : THEMATIC_TYPE_CONFIG[issue.issue_type];
  const conf = typeConfig || { label: issue.issue_type, color: '#888' };

  const scopeBadge = SCOPE_BADGE[issue.scope];
  const isApproved = issue.status === 'approved';
  const isDismissed = issue.status === 'dismissed';
  const isPending = issue.status === 'pending';

  return (
    <div className={`project-issue-card project-issue-card--${issue.status} project-issue-card--${issue.scope}`}>
      <div
        className="project-issue-card-header"
        onClick={() => setIsExpanded((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setIsExpanded((v) => !v)}
      >
        <span className="project-issue-card-icon" style={{ color: conf.color }}>●</span>
        <span
          className="project-issue-card-scope-badge"
          style={{ color: scopeBadge?.color, background: scopeBadge?.bg }}
        >
          {scopeBadge?.label}
        </span>
        <span className="project-issue-card-type" style={{ color: conf.color }}>
          {conf.label}
        </span>
        <span
          className="project-issue-card-severity"
          style={{ color: SEVERITY_COLORS[issue.severity] || '#888' }}
        >
          {issue.severity}
        </span>
        <span className="project-issue-card-title">{issue.title}</span>

        {isApproved && (
          <span className="project-issue-card-badge project-issue-card-badge--approved">Approved</span>
        )}
        {isDismissed && (
          <span className="project-issue-card-badge project-issue-card-badge--dismissed">Dismissed</span>
        )}

        <span className="project-issue-card-chevron">{isExpanded ? '▾' : '▸'}</span>
      </div>

      {isExpanded && (
        <div className="project-issue-card-body">
          {issue.description && <p className="project-issue-card-desc">{issue.description}</p>}
          {issue.suggestion && (
            <p className="project-issue-card-suggestion">
              <strong>Suggestion:</strong> {issue.suggestion}
            </p>
          )}

          {issue.metadata?.affectedChapters?.length > 0 && (
            <p className="project-issue-card-meta">
              <strong>Affects:</strong>{' '}
              {issue.metadata.affectedChapters.map((c) => `Ch. ${c}`).join(', ')}
            </p>
          )}
          {issue.metadata?.mergeTargets && (
            <p className="project-issue-card-meta">
              <strong>Merge:</strong> Ch. {issue.metadata.mergeTargets.join(' + ')}
            </p>
          )}

          {issue.metadata?.currentTone && issue.metadata?.targetTone && (
            <p className="project-issue-card-meta">
              <strong>Tone:</strong> {issue.metadata.currentTone} → {issue.metadata.targetTone}
            </p>
          )}
          {issue.metadata?.theme && (
            <p className="project-issue-card-meta">
              <strong>Theme:</strong> {issue.metadata.theme}
            </p>
          )}
          {issue.metadata?.motif && (
            <p className="project-issue-card-meta">
              <strong>Motif:</strong> {issue.metadata.motif}
            </p>
          )}

          {error && <p className="project-issue-card-error">{error}</p>}

          {isPending && (
            <div className="project-issue-card-actions">
              <button
                className="btn primary"
                onClick={(e) => { e.stopPropagation(); onApprove(); }}
                disabled={isBusy}
              >
                {isBusy ? 'Processing…' : 'Approve'}
              </button>
              <button
                className="btn subtle"
                onClick={(e) => { e.stopPropagation(); onDismiss(); }}
                disabled={isBusy}
              >
                {isBusy ? 'Processing…' : 'Dismiss'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
